import moment from 'moment';
import { Observable, ReplaySubject } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SupportCase, SupportCaseWithUrl } from '../support.interface';
import { SupportErrorService } from './error.service';
import { forMoment } from '../../utils/comparator';
import { CaseService } from './case.service';
import { isEqual } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class CaseManager {
    allOpenCases$ = new ReplaySubject<SupportCase[]>(1);

    private allOpenCases: SupportCaseWithUrl[];
    private allClosedCases: SupportCaseWithUrl[];

    constructor(
        private http: HttpClient,
        private caseService: CaseService,
        private supportErrorService: SupportErrorService,
    ) {}

    /*
     * Adds a new case WITHOUT re-fetching the raw data.
     */
    addCase(newCase: SupportCase): void {
        newCase.lastCaseActivity = moment();

        // If case is created from outside of support page then this property is undefined
        if (this.allOpenCases) {
            this.allOpenCases.push({
                url: null,
                ...newCase,
            });
            this.allOpenCases = this.buildCasesNav(this.allOpenCases);
            this.allOpenCases$.next(this.allOpenCases);
        }
    }

    /*
     * This method fetches arrays and cases and then builds the tree
     */
    refresh(): Observable<SupportCase[]> {
        return this.caseService.getCasesBrief().pipe(
            first(),
            map(response => {
                const newOpenCases = this.buildCasesNav(response);
                if (!isEqual(this.allOpenCases, newOpenCases)) {
                    this.allOpenCases = newOpenCases;
                    this.allOpenCases$.next(this.allClosedCases);
                }
                return this.allOpenCases;
            }),
            catchError(err => {
                this.supportErrorService.errorInterceptor(err);
                throw err;
            }),
        );
    }

    getCaseCounts(): Observable<ISupportCounts> {
        return this.http.get<ISupportCounts>(`/rest/v1/support/cases/counts`);
    }

    private buildCasesNav(cases: SupportCase[]): SupportCaseWithUrl[] {
        if (cases) {
            return cases
                .map(caseDetails => {
                    return {
                        ...caseDetails,
                        url: '/support/cases?caseId=' + caseDetails.id,
                    };
                })
                .sort(forMoment((supportCase: SupportCase) => supportCase.lastCaseActivity?.valueOf()).desc);
        }
    }
}
